<template>
  <teleport to="body">
    <div class="z-notification pointer-events-none fixed right-2 top-0 w-full">
      <div v-for="item in notifications" :key="item.id">
        <div
          class="my-2 flex w-full flex-col items-center space-y-4 sm:items-end"
         
        >
          <div
            class="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5"
          >
            <div class="p-4">
              <div class="flex items-start">
                <div class="shrink-0">
                  <component
                    :is="typeInfo(item.type)?.icon"
                    class="size-6"
                    :class="typeInfo(item.type)?.textClass"
                    aria-hidden="true"
                  />
                </div>
                <div class="ml-3 w-0 flex-1 pt-0.5">
                  <p
                    v-if="item.title"
                    class="mb-1 text-sm font-medium text-gray-900"
                  >
                    {{ item.title }}
                  </p>
                  <p class="text-sm text-gray-500">
                    {{ item.message }}
                  </p>
                  <div v-if="item.traceId" class="mt-3">
                    <span class="text-xs text-gray-500">
                      TraceID:
                      <span
                        class="clickable-text"
                        @click="copyTraceId(item.traceId!)"
                      >
                        {{ item.traceId }}
                      </span>
                    </span>
                  </div>
                  <div v-else-if="item.link" class="mt-3">
                    <Button
                      :href="item.link"
                      @click="removeNotification(item.id)"
                    >
                      {{ $t('general.open') }}
                    </Button>
                  </div>
                </div>
                <div class="ml-4 flex shrink-0">
                  <button
                    type="button"
                    class="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                   
                    @click="removeNotification(item.id)"
                  >
                    <span class="sr-only">Close</span>
                    <XMarkIcon class="size-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
export interface NotificationTypeInfo {
  type: NotificationType;
  icon: unknown;
  textClass: string;
}

const iconsMap: NotificationTypeInfo[] = [
  {
    type: 'success',
    icon: CheckCircleIcon,
    textClass: 'text-green-400',
  },
  {
    type: 'information',
    icon: InformationCircleIcon,
    textClass: 'text-blue-400',
  },
  {
    type: 'warning',
    icon: ExclamationTriangleIcon,
    textClass: 'text-yellow-400',
  },
  {
    type: 'error',
    icon: ExclamationCircleIcon,
    textClass: 'text-red-400',
  },
  {
    type: 'question',
    icon: QuestionMarkCircleIcon,
    textClass: 'text-blue-400',
  },
];
</script>

<script lang="ts" setup>
// DOCS: https://rollouter.atlassian.net/wiki/spaces/RCP/pages/8126550/Notifications

import { storeToRefs } from 'pinia';
import {
  CheckCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/vue/24/outline';
import { XMarkIcon } from '@heroicons/vue/20/solid';
import type { NotificationType } from '~~/stores/notifications';
import { useNotifications } from '~~/stores/notifications';
const notificationsStore = useNotifications();

const { notifications } = storeToRefs(notificationsStore);

const removeNotification = (id: string) => {
  nextTick(() => {
    notificationsStore.removeNotification(id);
  });
};

const typeInfo = (type: NotificationType) =>
  iconsMap.find((typeInfo) => typeInfo.type === type);

const i18n = useI18n();

const copyTraceId = async (traceId: string) => {
  try {
    await navigator.clipboard.writeText(traceId);
    notificationsStore.createNotification({
      type: 'success',
      message: i18n.t('error-page.trace-id-copy'),
    });
  } catch (error) {
    useErrorHandler(error, {
      type: 'error',
      message: 'Something went wrong.',
    });
  }
};
</script>
